import { useCustomerState } from "../../state/customer";
import type { Component } from "solid-js";
import { onCleanup } from "solid-js";
import { QueryBoundary } from "../../utils/QueryBoundary";
import type CustomerId from "../../state/CustomerId";
import DetailModal from "../../components/modal/DetailModal";
import type { IconName } from "../../components/icon/Icon";
import { useTranslation } from "../../state/i18n";
import type { Category } from "../../types";
import { isNil } from "lodash-es";
import { useModalManager } from "../../components/modal/ModalManager";
import { createFileResource } from "../../utils/fileResourceUtil";

type FileScope = "customer" | "submission";
type ImageProps = {
  fileId: string;
  scope: FileScope;
  ariaLabel: string;
  modalTitle: string;
  modalIcon: IconName;
  modalCategory: Category;
  cover?: boolean;
};

const getUri = (customerId: CustomerId, fileId: string, scope: FileScope): string => {
  switch (scope) {
    case "customer":
      return `/api/v1.0/customers/${customerId}/resources/${fileId}/download/direct`;
    case "submission":
      return `/api/v1.0/customers/${customerId}/registrationFile/${fileId}/download`;
  }
};

const Image: Component<ImageProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { customerId } = useCustomerState();

  const [file] = createFileResource(() => getUri(customerId, props.fileId, props.scope));

  onCleanup(() => {
    if (file.state !== "errored") {
      const url = file()?.data;
      if (!isNil(url)) {
        URL.revokeObjectURL(url);
      }
    }
  });

  return (
    <QueryBoundary>
      <button
        type="button"
        onClick={() =>
          open({
            content: () => (
              <DetailModal
                title={props.modalTitle}
                iconTile={{ icon: props.modalIcon, category: props.modalCategory }}
                content={() => (
                  <div class="h-134 overflow-hidden rounded-sm border border-subtle bg-surface-extra-subtle">
                    <img class="size-full overflow-hidden object-contain" src={file()?.data} alt={props.ariaLabel} />
                  </div>
                )}
              />
            ),
          })
        }
        aria-label={t(($) => $.OPEN_FILE_ARIA_LABEL, { type: props.ariaLabel.toLowerCase() })}
        classList={{
          "cursor-pointer overflow-hidden rounded-sm border border-subtle bg-surface-extra-subtle hover:brightness-95 focus-visible:outline-default": true,
          [!isNil(props.cover) ? "aspect-square w-full" : "h-20 w-full"]: true,
        }}
      >
        <img
          class={`size-full overflow-hidden ${!isNil(props.cover) ? "object-cover" : "object-contain"}`}
          src={file()?.data}
          alt={props.ariaLabel}
        />
      </button>
    </QueryBoundary>
  );
};

export default Image;
